import React, { useState } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { server } from '../../server'; 
import {
  IMAGE_ACCEPT_WITH_HEIC,
  getImagePreparationErrorMessage,
  prepareImageFileForUpload,
  readImageFileAsDataUrl,
} from '../../utils/imageUploadPreparation';

const RoomCreationForm = ({ onRoomCreated, onClose }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [bannerImage, setBannerImage] = useState(null);
  const [bannerPreview, setBannerPreview] = useState('');
  const [loading, setLoading] = useState(false);
  const [isPreparingImage, setIsPreparingImage] = useState(false);
  const [error, setError] = useState('');
  const currentProfile = useSelector(state => state.user.currentProfile) || sessionStorage.getItem('currentProfile') || 'User';

  const handleBannerChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';

    if (file) {
      setError('');
      setIsPreparingImage(true);

      try {
        const preparedFile = await prepareImageFileForUpload(file);
        setBannerImage(preparedFile);
        setBannerPreview(await readImageFileAsDataUrl(preparedFile));
      } catch (error) {
        console.error('Error preparing room banner:', error);
        setBannerImage(null);
        setBannerPreview('');
        setError(getImagePreparationErrorMessage(error));
      } finally {
        setIsPreparingImage(false);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const formData = new FormData();
    formData.append('name', name.trim());
    formData.append('description', description.trim());
    if (bannerImage) {
      formData.append('bannerImage', bannerImage);
    }

    try {
      const response = await axios.post(`${server}/room/rooms/create`, formData, {
        withCredentials: true,
        headers: {
          'current-profile': currentProfile
        }
      });

      if (response.status === 201) {
        setName('');
        setDescription('');
        setBannerImage(null);
        setBannerPreview('');
        onRoomCreated?.(response.data.room);
        onClose?.();
      }
    } catch (err) {
      console.error('Error creating room:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'An error occurred while creating the room.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="room-creation-form-container">
      <h2>Create a New Room</h2>
      <form onSubmit={handleSubmit} className="room-creation-form">
        <div className="room-creation-form-group">
          <label htmlFor="name">Room Name</label>
          <input
            type="text"
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            className="room-creation-form-input"
          />
        </div>

        <div className="room-creation-form-group">
          <label htmlFor="description">Room Description</label>
          <textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows="4"
            maxLength="500"
            placeholder="Enter a brief description of the room (max 500 characters)"
            className="room-creation-form-input"
          />
        </div>

        <div className="room-creation-form-group">
          <label htmlFor="banner">Room Banner</label>
          <input
            type="file"
            id="banner"
            accept={IMAGE_ACCEPT_WITH_HEIC}
            onChange={handleBannerChange}
            disabled={isPreparingImage || loading}
            className="room-creation-form-input"
          />
          {bannerPreview && (
            <div className="room-creation-form-banner-preview">
              <img src={bannerPreview} alt="Banner Preview" />
            </div>
          )}
        </div>

        {error && <p className="room-creation-form-error">{error}</p>}
        
        <button type="submit" className="room-creation-form-button" disabled={loading || isPreparingImage}>
          {loading ? 'Creating...' : isPreparingImage ? 'Preparing Image...' : 'Create Room'}
        </button>
      </form>
    </div>
  );
};

export default RoomCreationForm;


